import { loadRecordSnapshot } from "../../../../collaboration/loadRecordSnapshot";
import { RecordCollaborationPanel } from "../../../../collaboration/RecordCollaborationPanel";
import { createReportMutationQueue } from "../../../../collaboration/reportMutationQueue";
import { normalizeError } from "../../../../operationFeedback/normalizeError";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { fetchReport, fetchReportChecklist } from "../../../../api/service/report";
import {
  FullReportTitle,
  FullReportAddress,
  ReportStatus,
  ReportAppointmentApprovedStatus,
  ReportInvoiceStatus,
  ReportMustBeRectified,
} from "../../../../components/dashboard/service/reports/utils";
import {
  CheckBadgeIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  UsersIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import ChecklistItemLinesCompactV3 from "../../../../components/dashboard/quality/checklists/checklistItemLinesCompactV3";
import ChecklistStickyFillChrome from "../../../../components/dashboard/quality/checklists/ChecklistStickyFillChrome";
import ChecklistGapsBar from "../../../../components/dashboard/quality/checklists/ChecklistGapsBar";
import { collectChecklistGaps } from "../../../../components/dashboard/quality/checklists/checklistResponseConfig";
import { updateReportChecklist } from "../../../../api/service/report";
import { PMNotice } from "../../../../components/utils/notifications";
import { EnhancedChecklistSelector } from "../../../../components/dashboard/service/reports/enhanced-checklist-selector";
import { ReportMobileNav } from "../../../../components/dashboard/service/reports/report-mobile-nav";
import { ReportDesktopBackButton } from "../../../../components/dashboard/service/reports/report-desktop-back-button";
import { createReportChecklist } from "../../../../api/service/report";
import { CopyToClipboard } from "../../../../components/utils/Div";
import { mapsServiceUrl, serviceAgreementPath } from "../../../../components/Utils";

export const ReportChecklistPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const settings = useSelector((state) => state.session.currentUser.settings);
  const currentUser = useSelector((state) => state.session.currentUser);

  const [report, setReport] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingChecklist, setIsLoadingChecklist] = useState(true);
  const [error, setError] = useState(false);
  const [errors, setErrors] = useState([]);
  const [checklistSelectorOpen, setChecklistSelectorOpen] = useState(false);
  const [apiErrors, setApiErrors] = useState([]);
  const [saveUi, setSaveUi] = useState("idle");
  const checklistFlushRef = useRef(null);
  const [saveProblem, setSaveProblem] = useState(null);
  const [pendingWrites, setPendingWrites] = useState(0);
  const [checklistEditing, setChecklistEditing] = useState(false);
  const mutationQueue = useMemo(() => createReportMutationQueue({
    onPendingChange: setPendingWrites,
    onSaved: (_saved, version) => setReport(prev => ({ ...prev, edit_version: version })),
    onError: failure => setSaveProblem(normalizeError(failure)),
  }), [id]);
  useEffect(() => {
    const generation = mutationQueue.start();
    setSaveProblem(null);
    return () => {
      // Drain edits already made before ordinary navigation. Explicit discard
      // stops the queue immediately; failed writes remain blocked either way.
      const pending = checklistFlushRef.current?.();
      Promise.resolve(pending).catch(() => {}).finally(() => mutationQueue.stop(generation));
    };
  }, [mutationQueue]);

  // Determine if user can edit this report
  const canEdit =
    report?.user_can_edit_with_rights !== false ||
    report?.user_can_edit !== false;

  // Check if user is admin
  const isAdmin = useMemo(() => {
    return currentUser?.roles?.some((r) => r.name === "admin") || false;
  }, [currentUser?.roles]);

  // Load report
  useEffect(() => {
    if (!id) return;

    setIsLoading(true);
    setError(false);
    setErrors([]);

    const params = {
      fields: "*,contact.*,report_type.id,report_type.displayTitle,report_type.items,report_type.variables,report_type.checklist,user_can_edit_with_rights,approved,not_approved,updated_after_process,process_note,approval_status",
    };

    fetchReport(id, params)
      .then((data) => {
        setReport(data?.data);
        if (!data?.data?.checklist_id && !data?.data?.checklist?.id) mutationQueue.adoptSnapshot(data?.data?.edit_version);
        setIsLoading(false);
      })
      .catch(() => {
        toast.error("Kunne ikke laste rapport");
        setError(true);
        setIsLoading(false);
      });
  }, [id]);

  // Load checklist when report is loaded
  useEffect(() => {
    if (!report?.id) return;

    setIsLoadingChecklist(true);
    setApiErrors([]);

    fetchReportChecklist(report.id, {
      fields: "id,title,description,status,is_items_editable,items.*,checklist_type.id,checklist_type.displayTitle",
    })
      .then((response) => {
        // Check if response has errors (from backend)
        if (response?.errors) {
          // If 404 error, it means no checklist exists yet - that's fine
          if (response.errors.some(err => err.includes("does not have a checklist") || err.includes("404"))) {
            setChecklist(null);
            setApiErrors([]);
          } else {
            setApiErrors(response.errors);
            setChecklist(null);
          }
          setIsLoadingChecklist(false);
          return;
        }
        
        // Response can be either response.data (from axios) or just data (from our wrapper)
        const checklistData = response?.data || response;
        if (checklistData && checklistData.id) {
          // Ensure items is always an array
          if (!checklistData.items || !Array.isArray(checklistData.items)) {
            checklistData.items = [];
          }
          mutationQueue.adoptSnapshot(checklistData.report_edit_version);
          setReport(prev => ({ ...prev, edit_version: checklistData.report_edit_version }));
          setChecklist(checklistData);
        } else {
          setChecklist(null);
        }
        setIsLoadingChecklist(false);
      })
      .catch((error) => {
        console.error("Error loading checklist:", error);
        // If 404, it means no checklist exists yet - that's fine
        if (error?.response?.status === 404 || error?.status === 404) {
          setChecklist(null);
          setApiErrors([]);
        } else {
          const errorMessage = error?.response?.data?.errors?.join(", ") ||
                              error?.response?.data?.error ||
                              error?.message ||
                              "Kunne ikke laste sjekkliste";
          setApiErrors([errorMessage]);
          setChecklist(null);
        }
        setIsLoadingChecklist(false);
      });
  }, [report?.id]);

  const submitCreateChecklist = async (config) => {
    try {
      const payload = {
        report: {
          id: report?.id,
        },
        fields: "id,checklist",
      };

      if (config) {
        if (config.mode === "type") {
          payload.report.checklist_type_id = config.checklist_type_id;
          if (config.selected_item_ids && config.selected_item_ids.length > 0) {
            payload.report.selected_item_ids = config.selected_item_ids;
          }
        } else if (config.mode === "main_report" && config.items) {
          payload.report.copy_from_checklist_items = config.items;
        }
        // mode === "new" creates an empty checklist (no extra params needed)
      }

      const response = await mutationQueue.run(editVersion => createReportChecklist({ ...payload, report: { ...payload.report, edit_version: editVersion } }));

      if (response.data.id && response.data.checklist) {
        toast.success("Sjekkliste opprettet");
        // Reload checklist
        const checklistResponse = await fetchReportChecklist(report.id, {
          fields: "id,title,description,status,is_items_editable,items.*,checklist_type.id,checklist_type.displayTitle",
        });
        const checklistData = checklistResponse?.data || checklistResponse;
        if (checklistData && checklistData.id) {
          if (!checklistData.items || !Array.isArray(checklistData.items)) {
            checklistData.items = [];
          }
          mutationQueue.adoptSnapshot(checklistData.report_edit_version);
          setReport(prev => ({ ...prev, edit_version: checklistData.report_edit_version }));
          setChecklist(checklistData);
        }
        setChecklistSelectorOpen(false);
      }
    } catch (err) {
      setSaveProblem(normalizeError(err));
    }
  };

  const handleChecklistSelect = (config) => {
    submitCreateChecklist(config);
  };

  const updateChecklistItems = useCallback(async (id, payload) => {
    return mutationQueue.run(editVersion => updateReportChecklist(id, { ...payload, edit_version: editVersion }));
  }, [mutationQueue]);

  const handleItemsUpdate = useCallback((updatedItems) => {
    setChecklist((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: updatedItems,
      };
    });
    setReport((prevReport) => {
      if (!prevReport) return prevReport;
      return {
        ...prevReport,
        checklist: {
          ...(prevReport.checklist || {}),
          items: updatedItems,
        },
      };
    });
  }, []);

  if (isLoading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={errors} />;
  }

  if (!report?.id) {
    return <ResourceEmpty />;
  }

  const gapSummary = collectChecklistGaps(checklist?.items || []);
  const compactProgress =
    gapSummary.totalCount > 0
      ? `${gapSummary.resolvedCount}/${gapSummary.totalCount}`
      : null;
  const compactStatus =
    !checklist?.id
      ? t("quality.checklist.gaps.missing")
      : gapSummary.gaps.length === 0
      ? t("quality.checklist.gaps.statusDone")
      : t("quality.checklist.gaps.statusOpen");

  const headerMeta = [
    {
      component: <ReportStatus report={report} />,
      label: "Status",
      description: "",
      path: null,
      action: null,
      icon: null,
      textColor: "text-teal-700",
      hidden: false,
    },
    {
      component: <ReportAppointmentApprovedStatus report={report} />,
      label: "AppointmentStatus",
      description: "",
      path: null,
      action: null,
      icon: null,
      textColor: "text-teal-700",
      hidden: ["new", "ongoing"].includes(report.status) ? false : true,
      copy: false,
    },
    {
      component: <ReportMustBeRectified rectify={report?.must_be_rectified} />,
      label: "AppointmentStatus",
      description: "",
      path: null,
      action: null,
      icon: null,
      textColor: "text-teal-700",
      hidden: ["accomplished"].includes(report.status) ? false : true,
      copy: false,
    },
    {
      label: report?.customer?.customer_number + " " + report?.customer?.name,
      description: "",
      path: "/dashboard/customers/" + report?.customer?.id,
      action: null,
      icon: UsersIcon,
      textColor: "text-teal-700",
      hidden: report?.customer?.id ? false : true,
      copy: false,
    },
    {
      label: report?.project?.displayTitle,
      description: "",
      path: "/dashboard/projects/" + report?.project?.id,
      action: null,
      icon: BuildingOfficeIcon,
      textColor: "text-teal-700",
      hidden: report?.project?.id ? false : true,
      copy: false,
    },
    {
      label: (
        <CopyToClipboard
          path={mapsServiceUrl(
            FullReportAddress(report),
            settings?.preferred_maps_service
          )}
          content={FullReportAddress(report)}
          type="popup"
        />
      ),
      description: "",
      path: null,
      action: null,
      icon: MapPinIcon,
      textColor: "text-teal-700",
      hidden: report?.delivery_address || report.address ? false : true,
      copy: true,
    },
    {
      label: (
        <CopyToClipboard
          path={"tel:" + report?.customer?.mobile}
          content={report?.customer?.mobile}
          type="popup"
        />
      ),
      description: "",
      path: null,
      action: null,
      icon: PhoneIcon,
      textColor: "text-teal-700",
      hidden: report?.customer?.mobile ? false : true,
      copy: true,
    },
    {
      label: "Serviceavtale " + report?.agreement?.agreement_number,
      description: "",
      path: serviceAgreementPath({ serviceAgreement: report?.agreement }),
      action: null,
      icon: CheckBadgeIcon,
      textColor: "text-teal-700",
      hidden: report?.agreement?.id ? false : true,
      copy: true,
    },
  ];

  return (
    <>
      <RecordCollaborationPanel report={report} section="checklist"
        editing={saveUi === "pending" || saveUi === "saving" || saveUi === "error"}
        needsReview={Boolean(saveProblem)} errorMessage={saveProblem?.safeMessage} zIndex={3800}
        saving={pendingWrites > 0 || isLoadingChecklist || checklistSelectorOpen || checklistEditing}
        onRefresh={async () => {
          const token = mutationQueue.refreshToken();
          const fresh = await loadRecordSnapshot("report", report.id, { fields: "*,contact.*,report_type.*,user_can_edit_with_rights" });
          let nextChecklist = null;
          if (fresh.checklist_id || fresh.checklist?.id) {
            const response = await fetchReportChecklist(report.id, { fields: "id,title,description,status,is_items_editable,items.*,checklist_type.id,checklist_type.displayTitle" });
            nextChecklist = response?.data || response;
            if (!nextChecklist?.id || nextChecklist.report_edit_version !== fresh.edit_version) throw new Error("Rapporten ble endret under lasting. Hent siste versjon.");
          }
          return { apply: () => {
            mutationQueue.replaceCleanSnapshot(fresh.edit_version, token);
            setReport(fresh); setChecklist(nextChecklist); setApiErrors([]);
          } };
        }}
        onReload={() => { mutationQueue.stop(); window.location.reload(); }} />
      <ChecklistStickyFillChrome
        title={FullReportTitle(report)}
        subtitle={t("quality.checklist.page.reportSubtitle")}
        backTo={`/dashboard/service/reports/${report.id}`}
        progressLabel={compactProgress}
        statusLabel={compactStatus}
        items={checklist?.items || []}
      />
      <DashboardHeader
        subtitle="Sjekkliste"
        title={FullReportTitle(report)}
        meta={headerMeta}
        chromeClassName="hidden xl:block"
      >
        <div className="mx-auto max-w-5xl px-2 sm:px-4 py-3 sm:py-5 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] lg:pb-5">
          {/* Desktop: sticky checklist header + gaps under topbar (not above breadcrumbs) */}
          <div className="sticky top-[var(--dashboard-topbar-height,4rem)] z-20 mb-4 hidden sm:mb-6 xl:block">
            <div className="overflow-hidden bg-white shadow sm:rounded-lg">
              <div className="border-b border-gray-200 px-3 py-3 sm:px-6 sm:py-4">
                <div className="flex items-center gap-3">
                  <ClipboardDocumentIcon className="h-6 w-6 text-teal-600" />
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Sjekkliste for rapport
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                      {report?.report_number} - {report?.report_type?.displayTitle}
                    </p>
                  </div>
                </div>
              </div>
              {checklist?.items?.length > 0 ? (
                <div className="px-2 pb-1.5 pt-1 sm:px-3">
                  <ChecklistGapsBar items={checklist.items} position="static" />
                </div>
              ) : null}
            </div>
          </div>

          {/* Checklist Content */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-3 sm:px-6 py-4 sm:py-6">
              {!canEdit && (
                <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Kun visning
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          Denne sjekklisten kan ikke redigeres. Du kan se innholdet,
                          men ikke lagre endringer.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* API Errors */}
              {apiErrors.length > 0 && checklist && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Feil ved lagring
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <ul className="list-disc list-inside">
                          {apiErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {isLoadingChecklist ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Laster sjekkliste...</span>
                </div>
              ) : apiErrors.length > 0 && !checklist ? (
                <>
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          Feil ved lasting av sjekkliste
                        </h3>
                        <div className="mt-2 text-sm text-red-700">
                          <ul className="list-disc list-inside">
                            {apiErrors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-sm text-gray-600">
                    <p>Kunne ikke laste sjekkliste. Vennligst prøv igjen senere eller kontakt support hvis problemet vedvarer.</p>
                  </div>
                </>
              ) : !checklist && !isLoadingChecklist ? (
                <>
                  <div className="py-5 px-5 rounded-md shadow-xl">
                    <PMNotice
                      title="Sjekkliste mangler"
                      description="Rapporten er ikke knyttet til sjekkliste. Velg en sjekkliste type nedenfor for å opprette en."
                    />

                    <div className="mt-6">
                      <EnhancedChecklistSelector
                        open={checklistSelectorOpen}
                        onClose={() => setChecklistSelectorOpen(false)}
                        onSelect={handleChecklistSelect}
                        mainReportChecklist={report?.sequelMainReport?.checklist || null}
                        mainReport={report?.sequelMainReport || null}
                      />

                      <button
                        type="button"
                        onClick={() => setChecklistSelectorOpen(true)}
                        disabled={!canEdit}
                        className="w-full inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                        Velg sjekkliste
                      </button>
                    </div>
                  </div>
                </>
              ) : checklist ? (
                <div className="space-y-6">
                  {(saveUi === "pending" ||
                    saveUi === "saving" ||
                    saveUi === "saved") && (
                    <div
                      className={
                        saveUi === "error"
                          ? "inline-flex items-center gap-2 rounded-md bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-200"
                          : saveUi === "saved"
                          ? "inline-flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200"
                          : "inline-flex items-center gap-2 rounded-md bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200"
                      }
                      role="status"
                      aria-live="polite"
                    >
                      {(saveUi === "pending" || saveUi === "saving") && (
                        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                      )}
                      {saveUi === "saved" && <CheckIcon className="h-3.5 w-3.5" />}
                      {saveUi === "error" && (
                        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                      )}
                      {saveUi === "saving"
                        ? t("quality.checklist.item.saving")
                        : saveUi === "pending"
                        ? t("quality.checklist.item.savePending")
                        : saveUi === "saved"
                        ? t("quality.checklist.item.saved")
                        : t("quality.checklist.item.saveError")}
                    </div>
                  )}
                  <ChecklistItemLinesCompactV3
                    checklist={checklist}
                    items={checklist?.items || []}
                    reportId={report?.id}
                    editable={canEdit && checklist?.is_items_editable}
                    disabled={
                      !canEdit ||
                      (checklist?.is_items_editable !== undefined &&
                        !checklist?.is_items_editable)
                    }
                    isAdmin={isAdmin}
                    validationErrors={{}}
                    flushRef={checklistFlushRef}
                    onSaveUiChange={setSaveUi}
                    onEditingChange={setChecklistEditing}
                    onSaveError={(failure) => setSaveProblem(normalizeError(failure))}
                    updateChecklistItems={updateChecklistItems}
                    onItemsUpdate={handleItemsUpdate}
                    showGapsBar={false}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </DashboardHeader>

      <ReportDesktopBackButton reportId={report?.id} />
      <ReportMobileNav report={report} />
    </>
  );
};

